import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Auth from "src/Auth";

export type Memory = {
  id: string;
  content: string;
  source: string | null;
  created_at: string;
};

export const MEMORIES_QUERY_KEY = ["chatPersonalizationMemories"];
const QUERY_KEY = MEMORIES_QUERY_KEY;

export const useMemories = () => {
  const queryClient = useQueryClient();

  const { data: memories = [], isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const response = await Auth.fetchWithIdToken("/api/chat-personalization/memories/list", {});
      if (!response?.success) throw new Error("Failed to fetch memories");
      return response.memories as Memory[];
    },
    staleTime: 60_000,
    revalidateOnFocus: false,
  });

  const addMemory = useMutation({
    mutationFn: async (content: string) => {
      const response = await Auth.fetchWithIdToken("/api/chat-personalization/memories/create", { content });
      if (!response?.success) throw new Error("Failed to create memory");
      return response.memory as Memory;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY);
    },
  });

  const updateMemory = useMutation({
    mutationFn: async ({ id, content }: { id: string; content: string }) => {
      const response = await Auth.fetchWithIdToken("/api/chat-personalization/memories/update", { id, content });
      if (!response?.success) throw new Error("Failed to update memory");
      return response.memory as Memory;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(QUERY_KEY);
    },
  });

  const deleteMemory = useMutation({
    mutationFn: async (id: string) => {
      const response = await Auth.fetchWithIdToken("/api/chat-personalization/memories/delete", { id });
      if (!response?.success) throw new Error("Failed to delete memory");
    },
    onMutate: async (id: string) => {
      await queryClient.cancelQueries(QUERY_KEY);
      const previous = queryClient.getQueryData<Memory[]>(QUERY_KEY);
      queryClient.setQueryData<Memory[]>(QUERY_KEY, (old) => (old || []).filter((m) => m.id !== id));
      return { previous };
    },
    onError: (_err, _id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(QUERY_KEY, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(QUERY_KEY);
    },
  });

  const deleteAllMemories = useMutation({
    mutationFn: async () => {
      const response = await Auth.fetchWithIdToken("/api/chat-personalization/memories/delete-all", {});
      if (!response?.success) throw new Error("Failed to delete memories");
    },
    onMutate: async () => {
      await queryClient.cancelQueries(QUERY_KEY);
      const previous = queryClient.getQueryData<Memory[]>(QUERY_KEY);
      queryClient.setQueryData<Memory[]>(QUERY_KEY, []);
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(QUERY_KEY, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(QUERY_KEY);
    },
  });

  return { memories, isLoading, addMemory, updateMemory, deleteMemory, deleteAllMemories };
};
